/* eslint-disable @typescript-eslint/camelcase */
import React, { ChangeEvent, Component } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/input.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import styles from './styles.module.scss'
import {
  animated,
  config as springConfig,
  Transition
} from 'react-spring/renderprops.cjs.js'
import IntlMessageFormat from 'intl-messageformat'
import de from './locales/de.yml'
import fr from './locales/fr.yml'

type MessageListContent = string | MessageList

/** Recursive type for storing translated strings in objects */
interface MessageList {
  [key: string]: MessageListContent
}

interface LocalisedMessagesList {
  [key: string]: MessageList
}

export interface Municipality {
  readonly GDENR: number
  readonly ORTNAME: string
  readonly PLZ4: number
  readonly GDENAMK: string
  readonly KTKZ: string
  readonly NORMORTSNAME: string
  readonly NORMGEMEINDE: string
}

export interface Props {
  /** Which year to use for the municipality data. For the 2019 data, this
   *  would be `2019`. */
  municipalityData: string

  /* Deduplicate list of municipality names */
  dedupe?: boolean

  /** Handler for when a municipality is selected by the user */
  onSelectionHandler: (municipality: Municipality) => void

  /** Locale to use */
  locale: 'de' | 'fr'
}

interface State {
  isLoading: boolean
  results: Municipality[]
  resultSelected: boolean
  value: string
  error:
    | null
    | 'error.noResultsByZip'
    | 'error.noResultsByName'
    | 'error.municipalitiesNotDownloaded'
  municipalities: Municipality[]
}

/** All localised strings for the component */
const MESSAGES = { de, fr }

/**
 * Create a translation Service function, using the provided MessageList and
 * the currenty locale string.
 * @param messageList - The object containing the translated strings. The first
 *   level keys are the locale strings, then you're free to use whathever IDs
 *   you need to identify your strings.
 * @param locale - The locale string (i.e. 'en', 'de', 'fr' …)
 */
const translationServiceFactory = (
  messageList: LocalisedMessagesList,
  locale: string
) => {
  return (key: string | null) => {
    if (key == null) {
      return ''
    }
    return (
      key
        .split('.')
        .reduce(
          (messages: MessageListContent, prop: string) => messages[prop],
          messageList[locale]
        ) || 'TRANSLATED STRING NOT FOUND'
    )
  }
}

class MunicipalityDownloadError extends Error {}

/** RegEx to look whether a string starts with a number */
const startsWithNumber = /^\d/

/** Regex to look whether a string just contains text */
const containsOnlyLetters = /^\D+$/

/** Regex that extracts four numbers, presumably a ZIP code */
const containsFourDigits = /\d{4}/

/**
 * Check whether the results should be shown or not.
 *
 * There need to *some* results to be shown. Since we animate the objects, we
 * also need a an upper bound – no need to overwhelm the users (and the
 * browser) with hundreds of results. No one is going to need that.
 */
function showResults(results: any[]) {
  return results.length > 0 && results.length < 100
}

export class MunicipalitySearch extends Component<Props, State> {
  public static defaultProps = {
    municipalityData: '2021v2',
    locale: 'de'
  }

  public state = {
    isLoading: true,
    results: [],
    value: '',
    error: null,
    municipalities: []
  }

  private resetComponent = () => {
    this.setState({
      isLoading: false,
      results: [],
      value: ''
    })
  }

  private filterList = (searchTerm: string) => {
    let results: Municipality[] = []
    searchTerm = searchTerm.trim()

    // Only start filtering once we have enough data
    if (searchTerm.length > 1 && this.state.municipalities !== null) {
      // In case where we have four digits in the search term, we look for those
      const digitsResultsArray = containsFourDigits.exec(searchTerm)
      const { municipalities } = this.state
      if (digitsResultsArray) {
        const digits = digitsResultsArray[0]
        results = municipalities.filter(
          (municipality: Municipality) =>
            municipality.PLZ4.toString() === digits
        )

        // Filter Zürich
        results = results.filter(r => r.KTKZ === 'ZH')     

        if (results.length < 1) {
          this.setState({ error: 'error.noResultsByZip' })
        }
      } else if (startsWithNumber.test(searchTerm)) {
        // in case it starts with a number, we're assuming the reader is looking
        // for a zip code
        results = municipalities.filter((municipality: Municipality) =>
          RegExp(`^${searchTerm}`).test(municipality.PLZ4.toString())
        )

        // Filter Zürich
        results = results.filter(r => r.KTKZ === 'ZH')

        if (results.length < 1) {
          this.setState({ error: 'error.noResultsByZip' })
        }
      } else if (containsOnlyLetters.test(searchTerm)) {
        // In case it is letters, we look for placenames

        // Do some simple sanitizing to remove some RegEx gotchas
        // Also, we need to use two escape hatches because JS removes one? WTF?
        const sanitizedSearchTerm = searchTerm.replace('.', '\\.')
        const r = RegExp(sanitizedSearchTerm, 'i')
        results = municipalities.filter(
          (municipality: Municipality) =>
            r.test(municipality.ORTNAME) ||
            r.test(municipality.GDENAMK) ||
            r.test(municipality.NORMORTSNAME) ||
            r.test(municipality.NORMGEMEINDE)
        )

        // Filter Zürich
        results = results.filter(r => r.KTKZ === 'ZH')

        if (results.length < 1) {
          this.setState({ error: 'error.noResultsByName' })
        }
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'input',
        event_label: `search:for_${searchTerm}:results_${results.length}`
      })
    }

    return this.removeDuplicates(results, searchTerm)
  }

  private removeDuplicates(
    arr: Municipality[],
    searchTerm: string
  ): Municipality[] {
    const map = new Map()
    arr.forEach(v => {
      if (this.props.dedupe) {
        map.set(v.ORTNAME + v.GDENR, v)
      } else {
        map.set(v.PLZ4 + v.ORTNAME + v.GDENR, v)
      }
    })
    
    // show exact matches first
    const result = [...map.values()]
      .reduce(
        (acc, cur) => {
          if (
            cur.GDENAMK.toLowerCase() === searchTerm.toLowerCase() ||
            cur.ORTNAME.toLowerCase() === searchTerm.toLowerCase()
          ) {
            acc[0].push(cur)
          } else {
            acc[1].push(cur)
          }
          return acc
        },
        [[], []]
      )
      .flat()

    return result
  }

  private handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    this.setState({ isLoading: true, value })

    if (value.length < 1) {
      return this.resetComponent()
    }

    const results = this.filterList(value)

    if (results.length > 0) {
      this.setState({ error: null, isLoading: false, results })
    }

    this.setState({ results, isLoading: false, resultSelected: false })
  }

  /**
   * The datapipeline (Didier Orel) would like to store
   * the information about the NPA the users choose before
   * accessing to the generated story.
   */
  private sendToDataPipeline = (plz4: string) => {
    const BASE_URL = 'https://w.tda.io/b/votation'
    const ZIP = '?zip=' + plz4
    const url = BASE_URL + ZIP
    return window
      .fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        credentials: 'include'
      })
      .then(response => {
        console.log(response)
      })
      .catch(error => {
        console.log(error)
      })
  }

  // region React Component Lifecycle

  /** Component mounting */
  public componentDidMount() {
    // Download municipalities list
    // This is a preliminary measure, before we get a proper API
    return fetch(
      `https://interaktiv.tagesanzeiger.ch/static/gemeindesuche/${this.props.municipalityData}.json`
    )
      .then(res => {
        if (!res.ok) {
          throw new MunicipalityDownloadError(
            `Download error: ${res.status}: ${res.statusText}.`
          )
        }
        return res.json()
      })
      .then(data => {
        this.setState({
          isLoading: false,
          municipalities: data
        })
      })
      .catch(error => {
        if (error instanceof MunicipalityDownloadError) {
          console.log(error)
          console.info(
            'Make sure that you have the municipality and zip code data for the required year uploaded to the Interaktiv server.'
          )
          console.info(
            'Read more about the data generation in the "data" directory in the project repository for the MunicipalitySearch React component.'
          )
          this.setState({
            isLoading: false,
            error: 'error.municipalitiesNotDownloaded'
          })
        } else {
          throw error
        }
      })
  }

  public render() {
    const { isLoading, value, results, resultSelected } = this.state

    /** Translation Service
     * Needs to be inside the render function in order to be re-initialized
     * once the context has changed.
     */
    const t = translationServiceFactory(MESSAGES, this.props.locale)

    // result string formatter
    const resultString = new IntlMessageFormat(
      MESSAGES[this.props.locale].results,
      this.props.locale + '-CH'
    )

    const labelContent = () => {
      if (this.state.error) {
        return t(this.state.error)
      }

      if (!!value && value.length >= 2 && (!resultSelected)) {
        return resultString.format({ numResults: results.length })
      }

      return t('label')
    }

    const labelDirection = window.matchMedia('screen and (max-width: 599px)')
      .matches
      ? 'top pointing'
      : 'left pointing'

    return (
      <div className={`municipality-search ${styles.municipalitySearch}`}>
        <div className={styles.inputRow}>
          <div className='ui left icon input'>
            <input
              id='search'
              type='text'
              className={`prompt ${styles.flexInput}`}
              placeholder={t('placeholder') as string}
              value={value}
              disabled={
                this.state.error === 'error.municipalitiesNotDownloaded'
              }
              onChange={this.handleSearchChange}
            />
            <i className='search icon' />
          </div>

          <div>
            <label
              htmlFor='search'
              className={`ui ${
                this.state.error ? 'red' : 'basic grey'
              } ${labelDirection} label`}
            >
              {labelContent()}
            </label>
          </div>
        </div>
        {showResults(results) && (
          <div className={`results ${styles.results}`}>
            {!!value && showResults(results) && (
              <Transition
                native
                items={results}
                keys={(result: Municipality) =>
                  result.PLZ4 + result.GDENR + result.ORTNAME
                }
                from={{ transform: 'translate(0,-20px)', opacity: 0 }}
                enter={{ transform: 'translate(0,0px)', opacity: 1 }}
                leave={{ transform: 'translate(0, 60px)', opacity: 0 }}
                config={springConfig.gentle}
              >
                {result => props => (
                  <animated.div
                    onClick={() => {
                      this.props.onSelectionHandler(result)
                      this.resetComponent()
                      this.setState({'value': result.GDENAMK, resultSelected: true})

                      // this.sendToDataPipeline(result.PLZ4)
                    }}
                    onKeyUp={e => {
                      if (e.key === 'Enter' || e.key === 'Space') {
                        this.props.onSelectionHandler(result)
                        // this.sendToDataPipeline(result.PLZ4)
                      }
                    }}
                    className={`result ${styles.result}`}
                    style={props}
                    tabIndex={0}
                  >
                    <div className={styles.resultHeader}>
                      <span className={styles.resultPlz}>{result.PLZ4}</span>{' '}
                      {result.ORTNAME}
                    </div>
                    <div className={styles.resultMeta}>
                      {t('list.municipalityPrefix')} {result.GDENAMK}
                    </div>
                  </animated.div>
                )}
              </Transition>
            )}
          </div>
        )}
      </div>
    )
  }

  // endregion
}
